import React, { useEffect, useState } from "react";
import { Copy, RefreshCw, KeyRound, Bot, Mail, Send } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { api, API_BASE, errorMessage } from "../../api";

const copy = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Could not copy");
  }
};

const ApiKeyCard = () => {
  const [key, setKey] = useState(null);
  const [wpPw, setWpPw] = useState(null);
  const [show, setShow] = useState(false);
  const [showWp, setShowWp] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => { setKey(r.data.apiKey); setWpPw(r.data.wpAppPassword); }).catch((e) => toast.error(errorMessage(e)));
  }, []);

  const rotateWp = async () => {
    if (!window.confirm("Generate a new WordPress application password? Tools using the old one will stop working.")) return;
    try {
      const { data } = await api.post("/admin/settings/rotate-wp-password");
      setWpPw(data.wpAppPassword);
      setShowWp(true);
      toast.success("New application password generated");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate the key? Tools using the old key will stop working.")) return;
    try {
      const { data } = await api.post("/admin/settings/rotate-key");
      setKey(data.apiKey);
      setShow(true);
      toast.success("New key generated");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const endpoint = `${API_BASE}/api/blog/ingest`;
  const snippet = `curl -X POST ${endpoint} \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${show && key ? key : "YOUR_API_KEY"}" \\
  -d '{
    "title": "Your article title",
    "content": "## Markdown body\\n\\nWrite the article here…",
    "category": "Industry Trends",
    "excerpt": "Optional one-line summary",
    "cover": "https://…/image.jpg",
    "status": "draft"
  }'`;

  return (
    <div className="card p-6 md:p-7" data-testid="settings-api-card">
      <div className="flex items-start gap-4">
        <span className="w-11 h-11 rounded-xl bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center flex-shrink-0">
          <Bot size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display font-bold text-xl">Publish from AI tools</h2>
          <p className="text-sm text-[var(--fr-ink-2)] mt-1">
            Connect ChatGPT actions, Zapier, Make, n8n or your own scripts. Send a POST request with the API key below and the article lands here as a draft (or goes live with <code className="text-xs bg-[var(--fr-bg-alt)] px-1.5 py-0.5 rounded">"status": "published"</code>).
          </p>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-[1fr_auto] gap-3 items-end">
        <label className="block text-sm font-medium">
          API key
          <div className="mt-1.5 flex gap-2">
            <input readOnly className="fr-input !h-10 font-mono text-sm" value={key ? (show ? key : `${key.slice(0, 8)}${"•".repeat(24)}`) : "Loading…"} data-testid="settings-api-key" />
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setShow((v) => !v)} data-testid="settings-api-key-toggle">
              {show ? "Hide" : "Reveal"}
            </button>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => key && copy(key, "API key")} data-testid="settings-api-key-copy">
              <Copy size={14} />
            </button>
          </div>
        </label>
        <button type="button" className="btn btn-outline btn-sm h-10" onClick={rotate} data-testid="settings-api-key-rotate">
          <RefreshCw size={14} className="mr-1.5" /> Rotate key
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-[var(--fr-primary-tint)] bg-[var(--fr-primary-tint)]/40 p-5" data-testid="settings-wp-bridge">
        <div className="font-display font-bold">Marblism (Penny), Jasper & other “publish to WordPress” tools</div>
        <p className="text-sm text-[var(--fr-ink-2)] mt-1">The site speaks the WordPress publishing API, so any tool with a WordPress connector can publish here. In the tool, choose <strong>WordPress</strong> and enter:</p>
        <dl className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
          {[["Site URL", `${API_BASE}/api`, "settings-wp-site-url"], ["Username", "admin@fleetroot.com", "settings-wp-username"]].map(([k, v, tid]) => (
            <div key={k} className="rounded-xl bg-white border border-[var(--fr-border)] p-3 min-w-0">
              <dt className="text-xs uppercase tracking-wide text-[var(--fr-muted)]">{k}</dt>
              <dd className="mt-1 font-mono text-[12.5px] break-all" data-testid={tid}>{v}</dd>
            </div>
          ))}
          <div className="rounded-xl bg-white border border-[var(--fr-border)] p-3 min-w-0">
            <dt className="text-xs uppercase tracking-wide text-[var(--fr-muted)]">Application password</dt>
            <dd className="mt-1 font-mono text-[12.5px] break-all" data-testid="settings-wp-password">{wpPw ? (showWp ? wpPw : "•••• •••• •••• •••• •••• ••••") : "Loading…"}</dd>
            <div className="mt-2 flex gap-2">
              <button type="button" className="text-xs font-semibold text-[var(--fr-primary-dark)] hover:underline" onClick={() => setShowWp((v) => !v)} data-testid="settings-wp-password-toggle">{showWp ? "Hide" : "Reveal"}</button>
              <button type="button" className="text-xs font-semibold text-[var(--fr-primary-dark)] hover:underline" onClick={() => wpPw && copy(wpPw, "Application password")} data-testid="settings-wp-password-copy">Copy</button>
              <button type="button" className="text-xs font-semibold text-[var(--fr-muted)] hover:underline" onClick={rotateWp} data-testid="settings-wp-password-rotate">Regenerate</button>
            </div>
          </div>
        </dl>
        <p className="text-xs text-[var(--fr-muted)] mt-3">The application password uses WordPress's 24-character format (6 groups of 4), which these tools validate before connecting. Posts published from these tools appear instantly in Blog posts (HTML is converted to Markdown; featured images are stored on your storage). Send as “draft” if you want to review first.</p>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl border border-[var(--fr-border)] p-4">
          <div className="text-xs uppercase tracking-wide text-[var(--fr-muted)]">Endpoint</div>
          <div className="mt-1 font-mono text-[13px] break-all" data-testid="settings-endpoint">POST {endpoint}</div>
        </div>
        <div className="rounded-xl border border-[var(--fr-border)] p-4">
          <div className="text-xs uppercase tracking-wide text-[var(--fr-muted)]">Header</div>
          <div className="mt-1 font-mono text-[13px]">X-API-Key: &lt;your key&gt;</div>
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-[#0E0E10] text-[#E6E6E6] overflow-hidden">
        <div className="flex items-center justify-between px-4 h-10 border-b border-white/10 text-xs text-white/50 font-mono">
          <span>Example request</span>
          <button type="button" className="hover:text-white inline-flex items-center gap-1" onClick={() => copy(snippet, "Snippet")} data-testid="settings-snippet-copy">
            <Copy size={12} /> Copy
          </button>
        </div>
        <pre className="p-4 text-[12.5px] leading-relaxed font-mono overflow-x-auto" data-testid="settings-snippet">{snippet}</pre>
      </div>
      <p className="mt-3 text-xs text-[var(--fr-muted)]">
        Fields: <code>title</code> (required), <code>content</code> (Markdown, required), <code>excerpt</code>, <code>category</code>, <code>cover</code> (image URL), <code>author</code>, <code>date</code> (YYYY-MM-DD), <code>slug</code>, <code>status</code> (draft | published). Read time and slug are generated automatically.
      </p>
    </div>
  );
};

const PasswordCard = () => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card p-6 md:p-7" data-testid="settings-password-card">
      <div className="flex items-start gap-4">
        <span className="w-11 h-11 rounded-xl bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center flex-shrink-0">
          <KeyRound size={18} />
        </span>
        <div>
          <h2 className="font-display font-bold text-xl">Change password</h2>
          <p className="text-sm text-[var(--fr-ink-2)] mt-1">Use at least 8 characters.</p>
        </div>
      </div>
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <label className="block text-sm font-medium">
          Current password
          <input className="fr-input !h-10 mt-1.5" type="password" required value={form.current_password} onChange={set("current_password")} data-testid="settings-current-password" />
        </label>
        <label className="block text-sm font-medium">
          New password
          <input className="fr-input !h-10 mt-1.5" type="password" required minLength={8} value={form.new_password} onChange={set("new_password")} data-testid="settings-new-password" />
        </label>
        <label className="block text-sm font-medium">
          Confirm new password
          <input className="fr-input !h-10 mt-1.5" type="password" required minLength={8} value={form.confirm} onChange={set("confirm")} data-testid="settings-confirm-password" />
        </label>
      </div>
      <button type="submit" disabled={busy} className="btn btn-primary btn-sm mt-5 disabled:opacity-60" data-testid="settings-password-submit">
        {busy ? "Updating…" : "Update password"}
      </button>
    </form>
  );
};

const NotificationsCard = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/notifications").then((r) => setCfg(r.data)).catch(() => setCfg({ configured: false }));
  }, []);
  const test = async () => {
    setBusy(true);
    try {
      await api.post("/admin/notifications/test");
      toast.success(`Test email sent to ${cfg.to}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card p-6 md:p-7" data-testid="settings-notifications-card">
      <div className="flex items-start gap-4">
        <span className="w-11 h-11 rounded-xl bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center flex-shrink-0">
          <Mail size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display font-bold text-xl">Lead notifications</h2>
            {cfg && (
              <span className={`chip !text-[11px] uppercase tracking-wide ${cfg.configured ? "!bg-emerald-50 !text-emerald-700 !border-emerald-100" : "!bg-amber-50 !text-amber-700 !border-amber-100"}`} data-testid="settings-notifications-status">
                {cfg.configured ? "Active" : "Awaiting app password"}
              </span>
            )}
          </div>
          <p className="text-sm text-[var(--fr-ink-2)] mt-1">An email is sent instantly for every new demo request and partner application.</p>
          {cfg && (
            <dl className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
              {[["From", cfg.from], ["To", cfg.to], ["CC · demo requests", cfg.ccDemo], ["CC · partner applications", cfg.ccPartner]].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-[var(--fr-border)] p-3">
                  <dt className="text-xs uppercase tracking-wide text-[var(--fr-muted)]">{k}</dt>
                  <dd className="mt-1 font-medium break-all">{v || "—"}</dd>
                </div>
              ))}
            </dl>
          )}
          <button type="button" onClick={test} disabled={busy || !cfg?.configured} className="btn btn-outline btn-sm mt-5 disabled:opacity-60" data-testid="settings-notifications-test">
            <Send size={14} className="mr-1.5" /> {busy ? "Sending…" : "Send test email"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminSettings() {
  return (
    <div data-testid="admin-settings-page" className="space-y-6">
      <div>
        <span className="eyebrow">Account & integrations</span>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-2">Settings</h1>
      </div>
      <NotificationsCard />
      <ApiKeyCard />
      <PasswordCard />
    </div>
  );
}
